import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { useCallback, useEffect, useState } from 'react'

import { LPHolder, PairData } from '../sunset/types'
import { ERC20_ABI, executeWithRetry, PAIR_ABI } from './useMultiChainProvider'

export interface PairOnChainData {
  pairAddress: string
  chainId: number
  reserve0: BigNumber
  reserve1: BigNumber
  totalSupply: BigNumber
  token0Address: string
  token1Address: string
  token0Decimals: number
  token1Decimals: number
  token0Symbol: string
  token1Symbol: string
}

export interface HolderOnChainData {
  holderAddress: string
  pairAddress: string
  chainId: number
  balance: BigNumber
}

export interface OnChainDataResult {
  pairData: { [pairAddress: string]: PairOnChainData }
  holderData: HolderOnChainData[]
  loading: boolean
  error: Error | null
  refetch: () => void
}

async function fetchPairData(pair: PairData): Promise<PairOnChainData | null> {
  try {
    return await executeWithRetry(pair.chainId, async provider => {
      const pairContract = new Contract(pair.pairAddress, PAIR_ABI, provider)

      const [reserves, totalSupply, token0, token1] = await Promise.all([
        pairContract.getReserves(),
        pairContract.totalSupply(),
        pairContract.token0(),
        pairContract.token1()
      ])

      // Fetch token details
      const token0Contract = new Contract(token0, ERC20_ABI, provider)
      const token1Contract = new Contract(token1, ERC20_ABI, provider)

      const [token0Decimals, token1Decimals, token0Symbol, token1Symbol] = await Promise.all([
        token0Contract.decimals(),
        token1Contract.decimals(),
        token0Contract.symbol().catch(() => 'UNKNOWN'),
        token1Contract.symbol().catch(() => 'UNKNOWN')
      ])

      return {
        pairAddress: pair.pairAddress.toLowerCase(),
        chainId: pair.chainId,
        reserve0: reserves.reserve0,
        reserve1: reserves.reserve1,
        totalSupply,
        token0Address: token0.toLowerCase(),
        token1Address: token1.toLowerCase(),
        token0Decimals,
        token1Decimals,
        token0Symbol,
        token1Symbol
      }
    })
  } catch (error) {
    console.error(`Failed to fetch pair data for ${pair.pairAddress} after retries:`, error)
    return null
  }
}

async function fetchHolderBalance(
  holder: LPHolder,
  pairAddress: string,
  chainId: number
): Promise<HolderOnChainData | null> {
  try {
    return await executeWithRetry(chainId, async provider => {
      const pairContract = new Contract(pairAddress, PAIR_ABI, provider)
      const balance = await pairContract.balanceOf(holder.userAddress)

      return {
        holderAddress: holder.userAddress.toLowerCase(),
        pairAddress: pairAddress.toLowerCase(),
        chainId,
        balance
      }
    })
  } catch (error) {
    console.error(`Failed to fetch balance for ${holder.userAddress} after retries:`, error)
    return null
  }
}

export function usePairOnChainData(pairs: PairData[], holders: LPHolder[]): OnChainDataResult {
  const [pairData, setPairData] = useState<{ [pairAddress: string]: PairOnChainData }>({})
  const [holderData, setHolderData] = useState<HolderOnChainData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchAllData = useCallback(async () => {
    if (pairs.length === 0) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch all pair data in parallel
      const pairResults = await Promise.all(pairs.map(fetchPairData))

      const pairDataMap: { [pairAddress: string]: PairOnChainData } = {}
      pairResults.forEach(result => {
        if (result) {
          pairDataMap[result.pairAddress] = result
        }
      })
      setPairData(pairDataMap)

      // Fetch all holder balances in parallel
      const holderPromises = holders.map(holder => {
        const pair = pairs.find(
          p =>
            p.pairAddress.toLowerCase() === holder.pairAddress.toLowerCase() &&
            p.chainId === holder.chainId
        )
        if (!pair) return Promise.resolve(null)
        return fetchHolderBalance(holder, pair.pairAddress, pair.chainId)
      })

      const holderResults = await Promise.all(holderPromises)
      const validHolderData = holderResults.filter((h): h is HolderOnChainData => h !== null)
      setHolderData(validHolderData)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch on-chain data'))
    } finally {
      setLoading(false)
    }
  }, [pairs, holders])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    pairData,
    holderData,
    loading,
    error,
    refetch: fetchAllData
  }
}
